import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";

dayjs.extend(duration);

interface TimeBreakdown {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

interface CountdownTimerProps {
  targetDate: string;
}

const CountdownTimer: React.FC<CountdownTimerProps> = ({ targetDate }) => {
  const [time, setTime] = useState<TimeBreakdown>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [animate, setAnimate] = useState({
    days: false,
    hours: false,
    minutes: false,
    seconds: false,
  });

  useEffect(() => {
    const target = dayjs(targetDate);

    const updateTimer = () => {
      const now = dayjs();
      const diff = dayjs.duration(target.diff(now));

      const days = Math.floor(diff.asDays());
      const hours = diff.hours();
      const minutes = diff.minutes();
      const seconds = diff.seconds();

      setTime((prev) => {
        const newTime =
          diff.asSeconds() > 0
            ? { days, hours, minutes, seconds }
            : {
                days: 0,
                hours: 0,
                minutes: 0,
                seconds: 0,
              };
        setAnimate({
          days: newTime.days !== prev.days,
          hours: newTime.hours !== prev.hours,
          minutes: newTime.minutes !== prev.minutes,
          seconds: newTime.seconds !== prev.seconds,
        });
        return newTime;
      });
    };

    updateTimer();
    const timer = setInterval(updateTimer, 1000);

    const timeout = setTimeout(
      () =>
        setAnimate({
          days: false,
          hours: false,
          minutes: false,
          seconds: false,
        }),
      300
    );

    return () => {
      clearInterval(timer);
      clearTimeout(timeout);
    };
  }, [targetDate]);

  const TimeCard = ({
    value,
    label,
    shouldAnimate,
  }: {
    value: number;
    label: string;
    shouldAnimate: boolean;
  }) => (
    <div className="flex flex-col items-center px-2">
      <div className="relative h-8 flex items-center justify-center overflow-hidden perspective-500">
        <div
          className={`text-2xl font-bold leading-120 text-white ${
            shouldAnimate ? "animate-flip" : ""
          }`}
          key={value}
        >
          {value.toString().padStart(2, "0")}
        </div>
      </div>
      <span className="text-gray-1 leading-130 text-xs">{label}</span>
    </div>
  );

  return (
    <div className="bg-[#323238] px-2 py-1 border-b border-white/08 mt-1 rounded-xl flex justify-evenly items-center ">
      <TimeCard value={time.days} label="kun" shouldAnimate={animate.days} />
      <div className="h-8 w-px bg-white/08 mx-2 shrink-0" />
      <TimeCard value={time.hours} label="soat" shouldAnimate={animate.hours} />
      <div className="h-8 w-px bg-white/08 mx-2 shrink-0" />
      <TimeCard
        value={time.minutes}
        label="daqiqa"
        shouldAnimate={animate.minutes}
      />
      <div className="h-8 w-px bg-white/08 mx-2 shrink-0" />
      <TimeCard
        value={time.seconds}
        label="soniya"
        shouldAnimate={animate.seconds}
      />
    </div>
  );
};

export default CountdownTimer;
